import { roleHierarchy, type UserModel } from "@/dtos/user";
import { axiosWithAuth } from "@/libs/axios";
import { keycloak } from "@/libs/keycloak";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "sonner";

function useAuth() {
  const [user, setUser] = useState<UserModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { pathname } = useLocation();

  useEffect(() => {
    if (keycloak.didInitialize) {
      return;
    }

    (async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: "login-required",
          redirectUri: window.location.origin,
        });
        if (authenticated) {
          // set default auth header so that we don't have to set every time we perform http requests
          axiosWithAuth.defaults.headers.common["Authorization"] =
            `Bearer ${keycloak.token ?? ""}`;

          const user = await keycloak.loadUserProfile();
          const createdAt = user.createdTimestamp
            ? new Date(user.createdTimestamp).toISOString()
            : new Date().toISOString();

          let role: UserModel["role"] = "member";
          const tokenParsed = keycloak.tokenParsed;

          // check if user has access to "kol" dashboard
          if (
            tokenParsed &&
            tokenParsed.resource_access &&
            Object.hasOwn(tokenParsed.resource_access, "kol")
          ) {
            // grab all roles attached to the current user
            const kolRoles = tokenParsed.resource_access.kol.roles as Array<
              UserModel["role"]
            >;

            // pick the highest role the user has based on "roleHierarchy"
            role = kolRoles.reduce((highest, current) =>
              roleHierarchy[current] > roleHierarchy[highest]
                ? current
                : highest
            );

            setUser({
              role,
              id: user.id ?? "",
              email: user.email ?? "",
              name: user.username ?? "",
              created_at: createdAt,
            });
          }
        }
      } catch (error) {
        toast.error("Something went wrong, please try again", {
          richColors: true,
        });
        console.error(
          new Error("failed to initialize keycloak adapter", { cause: error })
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [pathname]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await keycloak.logout();
      toast.success("Logout successful", { richColors: true });
    } catch (error) {
      console.error(new Error("failed to logout", { cause: error }));
      toast.error("Logout failed, please try again", { richColors: true });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { user, isLoading, logout };
}

export { useAuth };
